// src/features/tasks/hooks/useTaskAttachments.js
import { useState, useCallback } from 'react';
import { useFiles } from '../../files/hooks/useFiles';
import { useFolders } from '../../files/hooks/useFolders';
import { taskAttachmentsAPI } from '../services/taskAttachments';
import { useApi } from '../../../hooks/api/useApi';

const TASKS_FOLDER_NAME = 'i18n-files:i18n_folder_names.tasks';

export function useTaskAttachments() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  
  const { uploadFile } = useFiles();
  const { getRootContents, createFolder, getFolderContents } = useFolders();
  const { execute } = useApi();

  /**
   * Find or create the "Tasks" meta folder
   * @returns {Promise<string>} folder_id of the Tasks folder
   */
  const ensureTasksMetaFolder = useCallback(async () => {
    try {
      // Get root contents
      const rootResult = await getRootContents();
      
      if (!rootResult?.success) {
        throw new Error('Failed to fetch root contents');
      }

      const rootData = rootResult.data.data;
      
      // Look for existing "Tasks" folder
      const existingFolder = rootData.folders?.find(
        folder => folder.folder_name === TASKS_FOLDER_NAME
      );

      if (existingFolder) {
        return existingFolder.folder_id;
      }

      // Create the Tasks meta folder if it doesn't exist
      const createResult = await createFolder({
        folder_name: TASKS_FOLDER_NAME,
        description: 'System folder for task attachments organized by task name',
        parent_folder_id: null, // Root level
        is_system_folder: false,
        is_protected: false,
      });

      if (!createResult?.success) {
        throw new Error('Failed to create Tasks folder');
      }

      return createResult.data.data.folder_id;
    } catch (err) {
      console.error('Error ensuring Tasks meta folder:', err);
      throw err;
    }
  }, [getRootContents, createFolder]);

  /**
   * Find or create a specific task's folder within the Tasks meta folder
   * @param {string} taskName - The name of the task
   * @returns {Promise<string>} folder_id of the task's folder
   */
  const ensureTaskFolder = useCallback(async (taskName) => {
    try {
      // First ensure the Tasks meta folder exists
      const tasksMetaFolderId = await ensureTasksMetaFolder();

      // Get contents of the Tasks meta folder
      const contentsResult = await getFolderContents(tasksMetaFolderId);
      
      if (!contentsResult?.success) {
        throw new Error('Failed to fetch Tasks folder contents');
      }

      const folderContents = contentsResult.data.data;
      
      // Look for existing task folder with this name
      const existingTaskFolder = folderContents.subfolders?.find(
        folder => folder.folder_name === taskName
      );

      if (existingTaskFolder) {
        return existingTaskFolder.folder_id;
      }

      // Create the task-specific folder if it doesn't exist
      const createResult = await createFolder({
        folder_name: taskName,
        description: `Attachments for task: ${taskName}`,
        parent_folder_id: tasksMetaFolderId,
        is_system_folder: false,
        is_protected: false,
      });

      if (!createResult?.success) {
        throw new Error('Failed to create task folder');
      }

      return createResult.data.data.folder_id;
    } catch (err) {
      console.error('Error ensuring task folder:', err);
      throw err;
    }
  }, [ensureTasksMetaFolder, getFolderContents, createFolder]);

  /**
   * List attachments for a task
   * @param {string} taskId - The task's ID
   * @returns {Promise<Object>} List of attachments
   */
  const listAttachments = useCallback(async (taskId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(() => taskAttachmentsAPI.listTaskAttachments(taskId));

      if (result?.success) {
        setAttachments(result.data.data.attachments || []);
      }

      setLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch attachments';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute]);

  /**
   * Upload and attach a file to a task
   * @param {File} file - The file to upload
   * @param {string} taskId - The task's ID
   * @param {string} taskName - The task's name (for folder structure)
   * @param {Object} options - Additional options
   * @param {string} options.attachmentLabel - Optional label for the attachment
   * @param {number} options.displayOrder - Display order (default 0)
   * @param {Function} options.onUploadProgress - Upload progress events, for a progress bar
   * @returns {Promise<Object>} Upload and attachment result
   */
  const uploadAndAttachFile = useCallback(async (file, taskId, taskName, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      // Ensure the task's folder exists in the structure: Tasks/TaskName
      const taskFolderId = await ensureTaskFolder(taskName);

      // Upload the file to the task's folder
      const uploadResult = await uploadFile(file, {
        folder_id: taskFolderId,
        is_system_file: false,
        onUploadProgress: options.onUploadProgress,
      });

      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || 'File upload failed');
      }

      const fileId = uploadResult.data.data.file_id;

      // Attach the file to the task
      const attachResult = await execute(() => 
        taskAttachmentsAPI.attachFileToTask(taskId, {
          file_id: fileId,
          attachment_label: options.attachmentLabel || null,
          display_order: options.displayOrder || 0,
        })
      );

      if (!attachResult?.success) {
        throw new Error(attachResult?.error || 'Failed to attach file to task');
      }

      // Refresh attachments list
      await listAttachments(taskId);

      setLoading(false);
      return {
        success: true,
        data: attachResult.data,
        fileId,
        folderId: taskFolderId,
      };
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [uploadFile, ensureTaskFolder, execute, listAttachments]);

  /**
   * Update attachment metadata
   * @param {string} taskId - The task's ID
   * @param {string} attachmentId - The attachment's ID
   * @param {Object} data - Update data
   * @returns {Promise<Object>} Update result
   */
  const updateAttachment = useCallback(async (taskId, attachmentId, data) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(() => 
        taskAttachmentsAPI.updateAttachment(taskId, attachmentId, data)
      );

      if (result?.success) {
        // Refresh attachments list
        await listAttachments(taskId);
      }

      setLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update attachment';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute, listAttachments]);

  /**
   * Remove an attachment from a task
   * @param {string} taskId - The task's ID
   * @param {string} attachmentId - The attachment's ID
   * @returns {Promise<Object>} Removal result
   */
  const removeAttachment = useCallback(async (taskId, attachmentId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(() => 
        taskAttachmentsAPI.detachFileFromTask(taskId, attachmentId)
      );

      if (result?.success) {
        // Refresh attachments list
        await listAttachments(taskId);
      }

      setLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to remove attachment';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute, listAttachments]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear attachments state
   */
  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  return {
    loading,
    error,
    attachments,
    // Actions
    listAttachments,
    uploadAndAttachFile,
    updateAttachment,
    removeAttachment,
    clearError,
    clearAttachments,
  };
}